import { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2, Search } from "lucide-react";
import Wrapper from "../../../components/Wrapper";
import { labApi } from "../../../services/labApi";

export default function ReagentsPage() {
  const [reagents, setReagents] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterPreparationMode, setFilterPreparationMode] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [showModal, setShowModal] = useState(false);
  const [editingReagent, setEditingReagent] = useState(null);
  const [showCompositionModal, setShowCompositionModal] = useState(false);
  const [selectedReagent, setSelectedReagent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type_id: "sachet_powder",
    preparation_mode: "normal",
    quantity_per_unit: "",
    unit: "gram",
    min_quantity: "",
  });
  const [compositionForm, setCompositionForm] = useState({ chemical: "", quantity: "" });

  const load = async () => {
    const [reagentRows, chemicalRows] = await Promise.all([labApi.getReagents(), labApi.getChemicals()]);
    setReagents(reagentRows);
    setChemicals(chemicalRows);
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const filteredReagents = useMemo(
    () =>
      reagents.filter((r) => {
        const matchesSearch = r.name.toLowerCase().includes(search.toLowerCase());
        const matchesType = filterType === "all" || r.type_id === filterType;
        const mode = r.preparation_mode || "normal";
        const matchesPreparationMode =
          filterPreparationMode === "all" || mode === filterPreparationMode;
        return matchesSearch && matchesType && matchesPreparationMode;
      }),
    [reagents, search, filterType, filterPreparationMode]
  );

  const openModal = (reagent) => {
    setEditingReagent(reagent || null);
    setFormData(
      reagent
        ? {
            name: reagent.name || "",
            type_id: reagent.type_id || "sachet_powder",
            preparation_mode: reagent.preparation_mode || "normal",
            quantity_per_unit: String(reagent.quantity_per_unit || ""),
            unit: reagent.unit || "gram",
            min_quantity: String(reagent.min_quantity || ""),
          }
        : {
            name: "",
            type_id: "sachet_powder",
            preparation_mode: "normal",
            quantity_per_unit: "",
            unit: "gram",
            min_quantity: "",
          }
    );
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      type_id: formData.type_id,
      preparation_mode: formData.preparation_mode,
      quantity_per_unit: Number(formData.quantity_per_unit || 0),
      unit: formData.unit,
      min_quantity: Number(formData.min_quantity || 0),
    };
    if (editingReagent) {
      await labApi.updateReagent(editingReagent.id, payload);
    } else {
      await labApi.createReagent(payload);
    }
    setShowModal(false);
    await load();
  };

  const handleDelete = async (id) => {
    await labApi.deleteReagent(id);
    await load();
  };

  const openCompositionModal = (reagent) => {
    setSelectedReagent(reagent);
    setCompositionForm({ chemical: "", quantity: "" });
    setShowCompositionModal(true);
  };

  const handleAddComposition = async (e) => {
    e.preventDefault();
    if (!selectedReagent) return;
    await labApi.addReagentComposition(selectedReagent.id, {
      chemical_id: compositionForm.chemical,
      quantity_required: Number(compositionForm.quantity || 0),
    });
    await load();
    setCompositionForm({ chemical: "", quantity: "" });
  };

  const handleRemoveComposition = async (chemicalId) => {
    if (!selectedReagent) return;
    await labApi.removeReagentComposition(selectedReagent.id, chemicalId);
    await load();
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="w-full text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Reagents</h2>
            <p className="mt-1 text-slate-500 text-left">Live data from backend APIs</p>
          </div>
          <button onClick={() => openModal()} className="flex items-center gap-2 self-start bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
            <Plus size={18} />
            Add Reagent
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reagents..." className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg" />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="all">All Types</option>
            <option value="sachet_powder">Sachet</option>
            <option value="liquid_bottle">Liquid</option>
            <option value="powder_bottle">Powder Bottle</option>
            <option value="pallet">Pallet</option>
          </select>
          <select
            value={filterPreparationMode}
            onChange={(e) => setFilterPreparationMode(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="all">All Preparation</option>
            <option value="instant">Instant</option>
            <option value="normal">Normal</option>
          </select>
          <div className="inline-flex gap-2">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-3 py-2 rounded-lg text-sm ${viewMode === "table" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`px-3 py-2 rounded-lg text-sm ${viewMode === "cards" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
            >
              Cards
            </button>
          </div>
        </div>

        {viewMode === "table" ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">Name</th>
                  <th className="px-4 py-3 text-left text-sm">Type</th>
                  <th className="px-4 py-3 text-left text-sm">Preparation</th>
                  <th className="px-4 py-3 text-left text-sm">Unit</th>
                  <th className="px-4 py-3 text-right text-sm">Qty/Unit</th>
                  <th className="px-4 py-3 text-right text-sm">Min Qty</th>
                  <th className="px-4 py-3 text-right text-sm">Composition</th>
                  <th className="px-4 py-3 text-right text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReagents.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3">{r.type_id}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          r.preparation_mode === "instant"
                            ? "bg-violet-100 text-violet-800"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {r.preparation_mode === "instant" ? "Instant" : "Normal"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{r.unit}</td>
                    <td className="px-4 py-3 text-right">{r.quantity_per_unit}</td>
                    <td className="px-4 py-3 text-right">{r.min_quantity ?? 0}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openCompositionModal(r)} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-sm">
                        {r.compositions.length} chemicals
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openModal(r)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit2 size={16} /></button>
                        <button onClick={() => handleDelete(r.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredReagents.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">{r.name}</h3>
                    <p className="text-sm text-slate-500">
                      {r.type_id} · {r.preparation_mode === "instant" ? "Instant" : "Normal"}
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => openModal(r)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit2 size={16} /></button>
                    <button onClick={() => handleDelete(r.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={16} /></button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600">{r.quantity_per_unit} {r.unit} per unit</p>
                <p className="mt-1 text-sm text-slate-500">Min Qty: {r.min_quantity ?? 0}</p>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => openCompositionModal(r)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm"
                  >
                    <span>Composition</span>
                    <span>{r.compositions.length} chemicals</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md p-4">
              <h3 className="text-lg font-semibold mb-3">{editingReagent ? "Edit Reagent" : "Add Reagent"}</h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Name" className="w-full px-3 py-2 border rounded-lg" required />
                <select value={formData.type_id} onChange={(e) => setFormData({ ...formData, type_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                  <option value="sachet_powder">Sachet</option>
                  <option value="liquid_bottle">Liquid</option>
                  <option value="powder_bottle">Powder Bottle</option>
                  <option value="pallet">Pallet</option>
                </select>
                <select
                  value={formData.preparation_mode}
                  onChange={(e) => setFormData({ ...formData, preparation_mode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="normal">Normal (prepared ahead)</option>
                  <option value="instant">Instant (made at dispatch)</option>
                </select>
                {formData.preparation_mode === "instant" && (
                  <p className="text-xs text-slate-500 m-0">
                    Capacity uses raw chemical stock. Chemicals are deducted when an order is completed.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input value={formData.quantity_per_unit} onChange={(e) => setFormData({ ...formData, quantity_per_unit: e.target.value })} type="number" placeholder="Qty" className="w-full px-3 py-2 border rounded-lg" />
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="gram">gram</option>
                    <option value="ml">ml</option>
                    <option value="pallet">pallet</option>
                    <option value="drop">drop</option>
                    <option value="scoop">scoop</option>
                    <option value="sachet">sachet</option>
                  </select>
                </div>
                <input
                  value={formData.min_quantity}
                  onChange={(e) => setFormData({ ...formData, min_quantity: e.target.value })}
                  type="number"
                  placeholder="Minimum Quantity"
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCompositionModal && selectedReagent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold">Composition: {selectedReagent.name}</h3>
              {(selectedReagent.compositions || []).map((comp) => (
                <div key={`${comp.chemical_id}`} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                  <span>{comp.name} ({comp.quantity} {comp.unit})</span>
                  <button onClick={() => handleRemoveComposition(comp.chemical_id)} className="text-red-600 text-sm">Remove</button>
                </div>
              ))}
              <form onSubmit={handleAddComposition} className="grid grid-cols-3 gap-2">
                <select value={compositionForm.chemical} onChange={(e) => setCompositionForm({ ...compositionForm, chemical: e.target.value })} className="px-3 py-2 border rounded-lg col-span-2" required>
                  <option value="">Select chemical</option>
                  {chemicals.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input value={compositionForm.quantity} onChange={(e) => setCompositionForm({ ...compositionForm, quantity: e.target.value })} placeholder="Qty" type="number" step="0.001" className="px-3 py-2 border rounded-lg" required />
                <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg">Add</button>
                <button type="button" onClick={() => setShowCompositionModal(false)} className="px-4 py-2 border rounded-lg">Done</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
